<template>
<md-list-item v-if="user.active_pay || (project && project.unlocked)" @click.native="ensureLoaded">
    <md-subheader class="ql-invite-header">Invite Collaborators</md-subheader>
    <md-list-expand class="ql-invite-form">
        <md-layout md-flex="90">
            <form nvalidate @submit.stop.prevent="inviteNew" v-show="!inviting">
                <md-input-container :class="{'md-input-invalid': showError}">
                    <label for="invite-email">Email</label>
                    <md-input
                        id="invite-email"
                        type="email"
                        required
                        v-model="newInvitee.email"
                        role="combobox"
                        aria-autocomplete="list"
                        aria-controls="invite-suggestions"
                        :aria-expanded="showSuggestions ? 'true' : 'false'"
                        :aria-activedescendant="activeDescendantId"
                        @focusin.native="onFieldFocus"
                        @focusout.native="onFieldBlur"
                        @keydown.native="onKeydown"
                    ></md-input>
                    <span class="md-error" v-if="showError">{{ errorMessage }}</span>
                </md-input-container>

                <!-- Inline suggestion list (plain markup — no vue-material, per the
                     Vue 3 migration guardrail). Rendered in normal flow between Email
                     and Name so it never overlays the rest of the form in the narrow
                     collaborators drawer. -->
                <div class="ql-invite-suggest">
                    <ul
                        id="invite-suggestions"
                        class="ql-invite-suggestions"
                        role="listbox"
                        v-show="showSuggestions && (loading || filteredSuggestions.length)"
                    >
                        <li class="ql-suggest-caption" aria-hidden="true" v-if="!loading && filteredSuggestions.length">People you've worked with</li>
                        <li class="ql-suggest-loading" v-if="loading" aria-disabled="true">Finding people you've worked with…</li>
                        <li
                            v-for="(s, i) in filteredSuggestions"
                            :key="s.uid || s.email"
                            :id="'invite-suggestion-' + i"
                            role="option"
                            :aria-selected="i === activeIndex ? 'true' : 'false'"
                            :class="['ql-suggest-item', { 'is-active': i === activeIndex }]"
                            @mousedown.prevent="selectSuggestion(s)"
                            @mousemove="activeIndex = i"
                        >
                            <span class="ql-suggest-name">{{ s.name || s.email }}</span>
                            <span class="ql-suggest-email" v-if="s.email" :title="s.email">{{ s.email }}</span>
                            <span class="ql-suggest-src" v-if="s.source === 'inviter'">Invited you before</span>
                            <span class="ql-suggest-src" v-else-if="s.source === 'affiliate'">Affiliate</span>
                        </li>
                    </ul>
                    <p class="ql-suggest-empty" v-show="showSuggestions && loaded && !loading && !filteredSuggestions.length && hasQuery">No previous contacts match</p>
                </div>

                <md-input-container>
                <label for="invite-name">Name</label>
                    <md-input
                        id="invite-name"
                        type="text"
                        required
                        v-model="newInvitee.name"
                        @focusin.native="onFieldFocus"
                        @focusout.native="onFieldBlur"
                        @keydown.native="onKeydown"
                    ></md-input>
                </md-input-container>
                <md-input-container>
                    <label for="invite-role">Role</label>
                    <md-input id="invite-role" type="text" required v-model="newInvitee.role"></md-input>
                </md-input-container>

                <md-button type="submit" class="md-raised" :disabled="checking"><md-icon>person_add</md-icon>&nbsp;{{ checking ? 'Sending…' : 'Send Invite' }}</md-button>
                <md-button class="md-raised" @click="resetInvitee">Clear</md-button>
            </form>
            <md-spinner md-indeterminate v-show="inviting && !emailSent"></md-spinner>
            <md-icon id="done-icon" v-show="inviting && emailSent">done</md-icon>
        </md-layout>
    </md-list-expand>
</md-list-item>
<md-list-item v-else>
    <md-subheader class="ql-invite-header">Invite Collaborators (disabled)</md-subheader>
</md-list-item>
</template>

<script>
import { mapGetters } from 'vuex';
import { buildExclusion, filterSuggestions } from '@/lib/contacts';

import { inviteSubject, inviteBody } from '@/lib/inviteEmail';

import { sendMail } from '@/lib/sendMail';

const CollaboratorsInvite = {
    name: 'CollaboratorsInvite',
    props: ['project'],
    data: function() {
        return {
            newInvitee: {email: '', name: '', role: ''},
            inviting: false,
            // True while an invite is being checked and sent. The account lookup
            // is a server call (lookupUserByEmail) and a cold start takes ~6s, so
            // the first click looked dead and a second click could slip two
            // invitations past the "already invited" check (Josh, 23 Sept 2026).
            checking: false,
            emailSent: false,
            showError: false,
            errorMessage: '',
            // Suggestion state
            suggestions: [],
            showSuggestions: false,
            loading: false,
            loaded: false,
            loadError: false,
            activeIndex: -1
        };
    },
    computed: {
        ...mapGetters([
            'user',
            'userByEmail',
            'userByUID',
            'collaborators',
            'ownerUID',
            'pendingCollaborators',
            'userByEmailPendingInvitation'
        ]),
        exclusion() {
            return buildExclusion({
                project: this.project,
                user: this.user,
                pending: this.pendingCollaborators
            });
        },
        queryTokens() {
            return [this.newInvitee.email, this.newInvitee.name];
        },
        hasQuery() {
            return this.queryTokens.some(t => (t || '').trim());
        },
        filteredSuggestions() {
            return filterSuggestions(this.suggestions, {
                exclusion: this.exclusion,
                tokens: this.queryTokens,
                limit: 8
            });
        },
        activeDescendantId() {
            return this.activeIndex >= 0 ? 'invite-suggestion-' + this.activeIndex : null;
        }
    },
    watch: {
        // Typing narrows the list; reset the keyboard highlight so it never points
        // past the filtered results.
        'newInvitee.email': function() { this.activeIndex = -1; },
        'newInvitee.name': function() { this.activeIndex = -1; }
    },
    methods: {
        async ensureLoaded() {
            if (this.loaded || this.loading) return;
            if (!this.user || !(this.user.active_pay || (this.project && this.project.unlocked))) return;
            this.loading = true;
            this.loadError = false;
            try {
                const list = await this.$store.dispatch('fetchInviteSuggestions', { cache: this.contactCache });
                this.suggestions = list || [];
                this.loaded = true;
            } catch (error) {
                // Fail closed — manual entry still works; just don't show the list.
                this.loadError = true;
                console.error('Failed to load invite suggestions:', error);
            } finally {
                this.loading = false;
            }
        },
        onFieldFocus() {
            if (this.blurTimer) {
                clearTimeout(this.blurTimer);
                this.blurTimer = null;
            }
            this.showSuggestions = true;
            this.ensureLoaded();
        },
        onFieldBlur() {
            // Delay so a row @mousedown.prevent selection registers before hide.
            this.blurTimer = setTimeout(() => {
                this.showSuggestions = false;
                this.activeIndex = -1;
            }, 150);
        },
        onKeydown(e) {
            const items = this.filteredSuggestions;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                if (!this.showSuggestions) this.onFieldFocus();
                if (items.length) this.activeIndex = Math.min(this.activeIndex + 1, items.length - 1);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                if (items.length) this.activeIndex = Math.max(this.activeIndex - 1, 0);
            } else if (e.key === 'Enter') {
                if (this.showSuggestions && this.activeIndex >= 0 && items[this.activeIndex]) {
                    e.preventDefault();
                    this.selectSuggestion(items[this.activeIndex]);
                }
            } else if (e.key === 'Escape') {
                if (this.showSuggestions) {
                    e.preventDefault();
                    this.showSuggestions = false;
                    this.activeIndex = -1;
                }
            }
        },
        async selectSuggestion(s) {
            // Collaborator rows carry only a name; resolve the email now (one read).
            let email = s.email;
            if (!email && s.uid) {
                try {
                    const u = await this.userByUID(s.uid);
                    email = (u && u.email) || '';
                } catch (error) {
                    email = '';
                }
            }
            this.newInvitee.email = email || '';
            this.newInvitee.name = s.name || '';
            // Role is intentionally NOT prefilled — a project-specific role/permission
            // must not carry across projects.
            this.showSuggestions = false;
            this.activeIndex = -1;
        },
        resetInvitee: function() {
            this.newInvitee = {email: '', name: '', role: ''};
            this.showSuggestions = false;
            this.activeIndex = -1;
        },
        generateEmailBody: function() {
            return inviteBody({
                project: this.project,
                recipientName: this.newInvitee.name,
                inviterName: this.user.username,
                inviterEmail: this.user.email,
                host: document.URL.split('/')[2] || 'app.thecuelist.com'
            });
        },
        handleEmailSent: function() {
            setTimeout(() => {
                this.emailSent = true;
                setTimeout(() => {
                    this.emailSent = false;
                    this.inviting = false;
                    this.resetInvitee();
                }, 1000);
            }, 2000);
        },
        showErrorMessage: function(message) {
            this.errorMessage = message;
            this.showError = true;
            setTimeout(() => {
                this.showError = false;
            }, 3000);
        },
        async inviteNew() {
            if (this.checking) return;
            this.checking = true;
            try {
                await this.sendInvite();
            } finally {
                this.checking = false;
            }
        },
        async sendInvite() {
            try {
                const email = this.newInvitee.email.toLowerCase();

                if (this.user.email === email) {
                    this.showErrorMessage('You cannot add yourself as a collaborator');
                    return;
                }

                const checkUser = await this.userByEmail(email);

                if (checkUser) {
                    const existingCollaborator = this.collaborators.find(collaborator => collaborator.uid === checkUser.uid);
                    if (existingCollaborator) {
                        this.showErrorMessage(`${checkUser.username} is already a collaborator on this project.`);
                        return;
                    }

                    if (this.ownerUID === checkUser.uid) {
                        this.showErrorMessage('You cannot add owner as a collaborator');
                        return;
                    }
                }

                const invitationPending = await this.userByEmailPendingInvitation(email, this.project.id);

                if (invitationPending) {
                    this.showErrorMessage('Invitation has already been sent to this email ID.');
                    return;
                }

                this.$emit('addCollaborator', {
                    ...this.newInvitee,
                    email
                });
                this.inviting = true;

                const postData = {
                    email,
                    subject: inviteSubject(this.project),
                    content: this.generateEmailBody()
                };

                sendMail(postData)
                    .then(() => this.handleEmailSent())
                    .catch((error) => {
                        this.inviting = false;
                        this.showErrorMessage('Failed to send invitation email. Please try again.');
                        console.error('Email sending failed:', error);
                    });
            } catch (error) {
                this.showErrorMessage('Failed to send invitation email. Please try again.');
                console.error('Email sending failed:', error);
            }
        },
        generateResendEmailBody: function(collaborator) {
            return inviteBody({
                project: this.project,
                recipientName: collaborator.name,
                inviterName: this.user.username,
                inviterEmail: this.user.email,
                host: document.URL.split('/')[2] || 'app.thecuelist.com'
            });
        },
        async resendInvitation(collaborator) {
            try {
                this.inviting = true;
                this.emailSent = false;

                const postData = {
                    email: collaborator.email,
                    subject: inviteSubject(this.project),
                    content: this.generateResendEmailBody(collaborator)
                };

                await sendMail(postData);
                this.handleEmailSent();

                this.$emit('resendInvitation', collaborator);
            } catch (error) {
                this.inviting = false;
                this.showErrorMessage('Failed to resend invitation email. Please try again.');
                console.error('Email resend failed:', error);
            }
        }
    },
    created: function() {
        // Non-reactive uid -> {uid,email,username} cache for the session, injected
        // into fetchInviteSuggestions so repeat opens don't re-read.
        this.contactCache = new Map();
        this.blurTimer = null;
    }
};
export default CollaboratorsInvite;

</script>

<style lang="scss">
.ql-invite-header {
    padding-left: 0px;
}

.ql-invite-form {
    width: 100%;
    text-align: right;
    padding-left: 10px;
}
.invite-link {
    margin-top: 10px;
    h2 {
        width: 100%;
    }
}

#done-icon {
    margin-left: 0;
    color: green;
}

// Inline suggestion list. text-align:left resets the right-aligned form wrapper.
.ql-invite-suggest {
    text-align: left;
}
.ql-invite-suggestions {
    list-style: none;
    margin: 4px 0 10px;
    padding: 4px 0;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 3px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.12);
    background: #fff;
    max-height: 240px;
    overflow-y: auto;
    font-size: 14px;

    .ql-suggest-caption {
        padding: 4px 12px;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        color: #999;
    }
    .ql-suggest-loading {
        padding: 10px 12px;
        color: #888;
        font-style: italic;
    }
    .ql-suggest-item {
        display: flex;
        flex-direction: column;
        justify-content: center;
        min-height: 44px;            // comfortable touch target (tablet in tech)
        padding: 6px 12px;
        cursor: pointer;

        .ql-suggest-name {
            font-weight: 600;
            color: #222;
            line-height: 1.2;
        }
        .ql-suggest-email {
            font-size: 12px;
            color: #888;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
        .ql-suggest-src {
            font-size: 11px;
            color: #5ecfdf;
        }

        &:hover {
            background: rgba(0, 0, 0, 0.04);
        }
        &.is-active {
            background: rgba(94, 207, 223, 0.18);   // keyboard highlight, distinct from hover
        }
    }
}
.ql-suggest-empty {
    margin: 2px 0 10px;
    padding: 0 2px;
    font-size: 12px;
    color: #999;
    text-align: left;
}
</style>



// WEBPACK FOOTER //
// invite.vue?763a3d22